export function asText(input: unknown): string {
  if (typeof input !== 'string') return '';
  return input;
}

export function sanitizeUrl(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed.startsWith('https://')) return null;
  return trimmed;
}

export function pubkeyShortHash(pubkey: string): string {
  if (!pubkey || pubkey.length < 8) return '????';
  try {
    const bytes = atob(pubkey);
    let hex = '';
    for (let i = 0; i < Math.min(4, bytes.length); i++) {
      hex += bytes.charCodeAt(i).toString(16).padStart(2, '0');
    }
    return hex.slice(0, 8);
  } catch {
    return pubkey.slice(0, 8);
  }
}

export interface ParsedMagnetURI {
  infoHash: string;
  displayName: string;
  raw: string;
}

export function parseMagnetURI(uri: string): ParsedMagnetURI | null {
  if (typeof uri !== 'string') return null;
  if (!uri.startsWith('magnet:?')) return null;

  const hashMatch = uri.match(/xt=urn:btih:([a-fA-F0-9]{40}|[A-Z2-7]{32})/i);
  if (!hashMatch) return null;

  const infoHash = hashMatch[1].toLowerCase();

  const dnMatch = uri.match(/dn=([^&]+)/);
  const displayName = dnMatch
    ? decodeURIComponent(dnMatch[1].replace(/\+/g, ' ')).replace(/[^a-zA-Z0-9 ._-]/g, '')
    : '';

  const raw = `magnet:?xt=urn:btih:${infoHash}${displayName ? `&dn=${encodeURIComponent(displayName)}` : ''}`;

  return { infoHash, displayName, raw };
}

const ALLOWED_CSS_PROPERTIES: Set<string> = new Set([
  '--color-accent',
  '--color-bg',
  '--color-text',
  '--font-mono',
  '--border-width',
  '--spacing-base',
  '--avatar-shape',
]);

const BLOCKED_CSS_PATTERNS = [
  /url\s*\(/i,
  /@import/i,
  /expression\s*\(/i,
  /javascript:/i,
  /data:/i,
  /behavior:/i,
  /-moz-binding/i,
];

export function sanitizeCSSProperties(raw: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [prop, value] of Object.entries(raw)) {
    if (!ALLOWED_CSS_PROPERTIES.has(prop)) continue;
    if (BLOCKED_CSS_PATTERNS.some(p => p.test(value))) continue;
    result[prop] = value;
  }
  return result;
}

export function linkifyText(text: string): Array<{ type: 'text' | 'url'; content: string }> {
  const urlPattern = /https?:\/\/[^\s<>"]+/g;
  const parts: Array<{ type: 'text' | 'url'; content: string }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'url', content: match[0] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return parts;
}
